// CrmAnaliticaTab.js — pestaña "Analítica" del CRM: cómo está funcionando
// atención al cliente, no cómo está funcionando la operativa de campo (eso
// ya lo cubre Analítica de Órdenes de trabajo). Todo viene agregado del
// backend (getCrmKpis.js) — nunca se descargan las incidencias completas al
// navegador para esto.
import React, { useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';

const TIPO_LABEL = { sat: 'SAT', mantenimiento: 'Mantenimiento', presupuesto: 'Presupuesto', instalacion: 'Instalación', otro: 'Otro' };
const ESTADO_LABEL = { pendiente_gestionar: 'Pendiente de gestionar', gestionada: 'Gestionada', rechazada: 'Rechazada', cerrada: 'Cerrada' };
const ESTADO_COLOR = { pendiente_gestionar: 'bg-yellow-400', gestionada: 'bg-blue-400', rechazada: 'bg-red-400', cerrada: 'bg-emerald-400' };

function KPICard({ icon, label, value, sub, colorBg, onClick }) {
  return (
    <button onClick={onClick} disabled={!onClick}
      className={`text-left bg-white rounded-2xl border shadow-sm p-4 transition-all ${onClick ? 'hover:shadow-md cursor-pointer' : 'cursor-default'}`}>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${colorBg}`}>{icon}</div>
      <p className="text-xl font-bold text-gray-900">{value}</p>
      <p className="text-xs font-semibold text-gray-600 mt-0.5">{label}</p>
      {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
    </button>
  );
}

export default function CrmAnaliticaTab() {
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const cargar = useCallback(() => {
    setLoading(true); setError('');
    fetch('/.netlify/functions/getCrmKpis', { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(data => {
        if (!data.success) { setError(data.error || 'Error desconocido'); return; }
        setKpis(data);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  if (loading) {
    return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-500" /></div>;
  }
  if (error) {
    return (
      <div className="space-y-3">
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2 text-sm">{error}</div>
        <button onClick={cargar} className="text-xs font-bold text-red-600 hover:text-red-800">Reintentar</button>
      </div>
    );
  }
  if (!kpis) return null;

  const totalTipo = Object.values(kpis.porTipo).reduce((s, n) => s + n, 0) || 1;
  const totalEstado = Object.values(kpis.porEstado).reduce((s, n) => s + n, 0) || 1;

  return (
    <div className="h-full overflow-y-auto space-y-4">
      <p className="text-xs text-gray-400">Últimos 90 días (desde {kpis.rangoDesde}) · {kpis.totalPeriodo} incidencias en el periodo</p>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        <KPICard icon={<span className="text-lg">📥</span>} colorBg="bg-blue-100" label="Nuevas hoy" value={kpis.nuevasHoy} />
        <KPICard icon={<span className="text-lg">📅</span>} colorBg="bg-blue-100" label="Nuevas esta semana" value={kpis.nuevasSemana} />
        <KPICard icon={<span className="text-lg">🗓️</span>} colorBg="bg-blue-100" label="Nuevas este mes" value={kpis.nuevasMes} />
        <KPICard icon={<span className="text-lg">✅</span>} colorBg="bg-emerald-100" label="Tasa de cierre" value={kpis.tasaCierrePct !== null ? `${kpis.tasaCierrePct}%` : '—'} sub="cerradas + rechazadas del periodo" />
        <KPICard icon={<span className="text-lg">⏱</span>} colorBg="bg-amber-100" label="Tiempo medio hasta gestión" value={kpis.tiempoMedioGestionHoras !== null ? `${kpis.tiempoMedioGestionHoras}h` : '—'} sub={kpis.tiempoMedioGestionHoras !== null ? `sobre ${kpis.muestraGestion} incidencias` : 'acumulando datos'} />
        <KPICard icon={<span className="text-lg">🏁</span>} colorBg="bg-purple-100" label="Tiempo medio hasta cierre" value={kpis.tiempoMedioCierreHoras !== null ? `${kpis.tiempoMedioCierreHoras}h` : '—'} sub={kpis.tiempoMedioCierreHoras !== null ? `sobre ${kpis.muestraCierre} incidencias (incluye estimación para cerradas antiguas)` : 'acumulando datos'} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border shadow-sm p-5">
          <h3 className="font-bold text-gray-900 mb-3 text-sm">Por tipo</h3>
          <div className="space-y-2">
            {Object.entries(kpis.porTipo).filter(([, n]) => n > 0).map(([tipo, n]) => (
              <div key={tipo} className="flex items-center gap-3">
                <span className="text-sm text-gray-700 w-32 flex-shrink-0">{TIPO_LABEL[tipo] || tipo}</span>
                <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                  <div className="bg-red-400 h-full rounded-full" style={{ width: `${Math.round((n / totalTipo) * 100)}%` }} />
                </div>
                <span className="text-xs font-bold text-gray-600 w-8 text-right flex-shrink-0">{n}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border shadow-sm p-5">
          <h3 className="font-bold text-gray-900 mb-3 text-sm">Por estado</h3>
          <div className="space-y-2">
            {Object.entries(kpis.porEstado).map(([estado, n]) => (
              <div key={estado} className="flex items-center gap-3">
                <span className="text-sm text-gray-700 w-40 flex-shrink-0">{ESTADO_LABEL[estado]}</span>
                <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                  <div className={`${ESTADO_COLOR[estado]} h-full rounded-full`} style={{ width: `${Math.round((n / totalEstado) * 100)}%` }} />
                </div>
                <span className="text-xs font-bold text-gray-600 w-8 text-right flex-shrink-0">{n}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
        <div className="bg-amber-50 border-b border-amber-100 px-5 py-3 flex items-center justify-between">
          <h3 className="font-bold text-sm text-amber-800">Pendientes de gestionar hace más de 24h</h3>
          {kpis.pendientesAntiguasTotal > 0 && (
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{kpis.pendientesAntiguasTotal}</span>
          )}
        </div>
        <div className="divide-y divide-gray-50">
          {kpis.pendientesAntiguas.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">Ninguna — todo se ha gestionado a tiempo.</p>
          ) : (
            kpis.pendientesAntiguas.map(p => (
              <div key={p.id} className="flex items-center justify-between px-5 py-2.5 text-sm">
                <div className="min-w-0">
                  <p className="text-gray-700 truncate">{p.clienteNombre || '—'}</p>
                  <p className="text-xs text-gray-400 font-mono">{p.referencia}</p>
                </div>
                <span className="font-mono font-semibold text-amber-700 flex-shrink-0">{p.horas}h</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
